/* eslint-disable no-console */
import { VAULT_MEDIA_TYPE } from '../../constants/vault';
import { DEFAULT_MESSAGE, INVALID_VIDEO_MESSAGE } from '../../state/previewAsset';
import {
  getVideoSourceFromPreview,
  getLiveVideoUrl,
  getAssetFullRendition,
} from '../../components/AssetDetails/api/assetsLookupsMetatags';
import { getSourceLink } from '../../api/vault/assetDetails/api';
import generatePreviewUrl from '../generatePreviewUrl';

export async function generateAssetMediaInformation(rawAssetDetails) {
  let mediaInformation = {
    type: null,
    source: null,
    sourceSource: null,
    photoRotation: null,
    message: DEFAULT_MESSAGE,
  };
  const isAssetSelected = rawAssetDetails?._id !== undefined;

  if (isAssetSelected) {
    const { type, _id } = rawAssetDetails;
    const {
      video, virtualAsset, image, document, audio, clip, liveVideo,
    } = VAULT_MEDIA_TYPE;

    if (type === video || type === clip || type === liveVideo) {
      const { sha1 } = rawAssetDetails;

      if (_id && sha1) {
        try {
          const videoUrlFromPreview = await getVideoSourceFromPreview(_id);

          // VV-539: /assets/source now requires the downloadSource permission (Super
          // Admin), so this rejects for most users. Caught on its own rather than inside
          // the block below, because a refusal here must not take the preview URL down
          // with it — that would blank the player for everyone who is not a Super Admin.
          // `sourceSource` staying null is the correct outcome: it feeds `hasSource` in
          // OperationArea, so the Source option simply is not offered to a user who
          // cannot download it.
          // 'probe': the URL is only tested for truthiness here — it becomes `hasSource`
          // in OperationArea, which decides whether the Source download option renders.
          // The download itself re-fetches the link through downloadSourceAsset, and that
          // call is the one recorded as a Download.
          let sourceUrl = null;
          try {
            const sourceUrlRes = await getSourceLink({ id: _id }, 1, 'probe');
            sourceUrl = sourceUrlRes?.data?.data;
          } catch {
            sourceUrl = null;
          }

          if (videoUrlFromPreview) {
            mediaInformation = {
              type,
              source: videoUrlFromPreview,
              sourceSource: sourceUrl,
              photoRotation: null,
              message: DEFAULT_MESSAGE,
            };
          } else {
            console.log('[QA] videoUrl not available');
            mediaInformation = {
              type: null,
              source: null,
              sourceSource: null,
              photoRotation: null,
              message: INVALID_VIDEO_MESSAGE,
            };
          }
        } catch (e) {
          console.log('[QA]', e);
          console.log('[QA] videoUrl not available');
          mediaInformation = {
            type: null,
            source: null,
            sourceSource: null,
            photoRotation: null,
            message: INVALID_VIDEO_MESSAGE,
          };
        }
      } else if (_id && type === liveVideo) {
        try {
          const liveVideoUrl = await getLiveVideoUrl(_id);

          if (liveVideoUrl) {
            mediaInformation = {
              type,
              source: liveVideoUrl,
              sourceSource: null,
              photoRotation: null,
              message: DEFAULT_MESSAGE,
            };
          } else {
            console.log('[QA] liveVideoUrl not available');
            mediaInformation = {
              type: null,
              source: null,
              sourceSource: null,
              photoRotation: null,
              message: INVALID_VIDEO_MESSAGE,
            };
          }
        } catch (e) {
          console.log('[QA]', e);
          console.log('[QA] liveVideoUrl not available');
          mediaInformation = {
            type: null,
            source: null,
            sourceSource: null,
            photoRotation: null,
            message: INVALID_VIDEO_MESSAGE,
          };
        }
      } else {
        console.log('[QA] sha1 not available');
        mediaInformation = {
          type: null,
          source: null,
          sourceSource: null,
          photoRotation: null,
          message: INVALID_VIDEO_MESSAGE,
        };
      }
    } else if (type === virtualAsset) {
      const { previewImageId } = rawAssetDetails;
      mediaInformation = {
        type: virtualAsset,
        source: generatePreviewUrl(previewImageId),
        sourceSource: null,
        photoRotation: null,
        message: DEFAULT_MESSAGE,
      };
    } else if (type === image) {
      const { previewImageUrl, photoRotation } = rawAssetDetails;
      const previewStartsWith = 'http://central.xosvault.com/repository/resource?uuid=';
      let PreviewImage;
      let FullSizeImage;
      if (previewImageUrl) {
        FullSizeImage = await getAssetFullRendition(_id, 'view');
        if (previewImageUrl.startsWith(previewStartsWith)) {
          PreviewImage = FullSizeImage;
        } else {
          PreviewImage = previewImageUrl;
        }
      }
      mediaInformation = {
        type: image,
        source: PreviewImage,
        fullSizeSource: FullSizeImage,
        sourceSource: null,
        photoRotation,
        message: DEFAULT_MESSAGE,
      };
    } else if (type === document) {
      const { previewImageId } = rawAssetDetails;
      mediaInformation = {
        type: document,
        source: generatePreviewUrl(previewImageId),
        sourceSource: null,
        photoRotation: null,
        message: DEFAULT_MESSAGE,
      };
    } else if (type === audio) {
      const { previewImageId, sha1 } = rawAssetDetails;
      const streamUrl = _id && sha1
        ? await getAssetFullRendition(_id, 'view')
        : generatePreviewUrl(previewImageId);
      mediaInformation = {
        type: audio,
        source: streamUrl,
        sourceSource: null,
        photoRotation: null,
        message: DEFAULT_MESSAGE,
      };
    } else {
      console.log('[QA] media type: unknown');
      mediaInformation = {
        type: null,
        source: null,
        sourceSource: null,
        photoRotation: null,
        message: DEFAULT_MESSAGE,
      };
    }
  }

  return mediaInformation;
}
